import './Application.scss';
import './Application.m.scss';
import { Container } from '@components/Component/Container/Container.js';
import { Request } from '@components/Request/Request.js';
import { BehaviorSubject, of, ReplaySubject } from 'rxjs';
import { isMobile } from '@components/Utility/MobileDetect.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { CONFIG, ENV, PERM, TOKEN } from '@components/Environment.js';
import { convertLegacyQ360Url, createComponentsFromDOM, getJWTPayload, setCookie, zoomHandler } from '@components/Utility/index.js';
import { DateTimeManager } from '@components/DateTimeManager/DateTimeManager.js';
import { switchMap } from 'rxjs/operators';
/**
 * Class `Application` extends the `Container` class.
 * It represents the overall application interface with the main components such as `Navbar`, and `Viewport`.
 */
export class Application extends Container {
    navStackPush(entry) {
        this.navStack.push(entry);
        this.navStackDepth$.next(this.navStack.length);
    }
    navStackPop() {
        const entry = this.navStack.pop();
        this.navStackDepth$.next(this.navStack.length);
        return entry;
    }
    navStackReset() {
        this.navStack.length = 0;
        this.navStackDepth$.next(0);
    }
    navStackDepth() {
        return this.navStack.length;
    }
    get SessionTimeoutManager() {
        if (!this.dependencies.SessionTimeoutManager) {
            // we didnt receive a request dependency injection, so just use the global singleton
            this.dependencies.SessionTimeoutManager = DIContainer.getInstance('SessionTimeoutManager');
        }
        return this.dependencies.SessionTimeoutManager;
    }
    /**
     * Request getter which handles lazy loading the `Request` dependency. Supports injection for unit tests etc.
     */
    get Request() {
        if (!this.dependencies.Request) {
            // we didnt receive a request dependency injection, so just use the global singleton
            this.dependencies.Request = DIContainer.getInstance('Request');
        }
        return this.dependencies.Request;
    }
    /**
     * Which toolbar sections to show (navigation, bookmarks, recents). Subclasses override to enable Q360-style tabs.
     */
    getToolbarSections() {
        return { navigation: true, bookmarks: false, recents: false };
    }
    /**
     * Observable of toolbar menu data. Toolbar calls this instead of hitting an endpoint directly.
     * Subclasses override to either request from an endpoint (e.g. Q360) or emit a hardcoded list (e.g. mobile app).
     */
    loadToolBarItems() {
        return of({ navigation: [] });
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'Application';
        this.authRequired = true;
        this.configLoaded$ = new ReplaySubject(1);
        /** Navigation stack for back-navigation support. Entries are pushed when navigating into child forms. */
        this.navStack = [];
        this.navStackDepth$ = new BehaviorSubject(0);
        // global application level hotkeys
        this.hotkeys = {
            // alt+g defined in Q360.ts subclass
            '*': (event, key) => {
                const screenManager = DIContainer.getInstance('ScreenManager');
                const screen = screenManager.activeScreenGet();
                if (screen && screen.currentForm) {
                    if (screen.currentForm.hotkeys[key] && screen.currentForm.hotkeys[key](event, key)) {
                        return true;
                    }
                }
            }
        };
        DIContainer.singletons['Application'] = this; // avoid loop if something tries to getInstance of the Application before the constructor is done (ie. toolbar initializing buttons)
        this.updateDatetimeFormat('');
        // quick fix for navbar user menu caption because navbar shouldnt be relying on reading cookie.
        // ideally we should remove this logic entirely and set the userName ENV() variable as part of the application_get response
        // we will just then need to trigger the navbar user menu button to refresh once we have a caption for it.
        // Set userName from the JWT cookie if available (browser only, before navbar init)
        const jwtPayload = getJWTPayload();
        if (jwtPayload?.user_name) {
            ENV('userName', jwtPayload.user_name);
        }
        this.initializeCache()
            .subscribe({
            next: () => {
                this.loadApplicationConfig();
            },
            error: (e) => {
                console.error(e);
                this.loadApplicationConfig();
            }
        });
        const navbar = DIContainer.getInstance('Navbar', 'navbar');
        const viewport = DIContainer.getInstance('Viewport', 'viewport');
        this.componentsAdd([
            navbar,
            viewport
        ]);
    }
    initializeCache() {
        return this.checkClientVersion().pipe(switchMap(() => this.checkClientUserId()));
    }
    checkClientUserId() {
        if (!ENV('userId')) {
            return of(true);
        }
        return DIContainer.getInstance('CacheManager').getCache('client', 'userId', { defaultReturnValue: '' }).pipe(switchMap((cachedUserId) => {
            if (cachedUserId !== ENV('userId')) {
                console.log(`New login detected. Deleting cache for ${cachedUserId}, new userid: ${ENV('userId')}.`);
                return this.setNewClientVersion();
            }
            else {
                return of(true);
            }
        }));
    }
    checkClientVersion() {
        return DIContainer.getInstance('CacheManager').getCache('client', 'clientVersion', { defaultReturnValue: '' }).pipe(switchMap((cachedVersion) => {
            if (!cachedVersion || cachedVersion !== ENV('clientVersion')) {
                return this.setNewClientVersion();
            }
            else {
                return of(true);
            }
        }));
    }
    setNewClientVersion() {
        const cacheManager = DIContainer.getInstance('CacheManager');
        return cacheManager.deleteCache('request').pipe(switchMap(() => cacheManager.deleteCache('application', this.getAppConfigCacheKey())), switchMap(() => cacheManager.setCache('client', 'clientVersion', ENV('clientVersion'))), switchMap(() => cacheManager.setCache('client', 'userId', ENV('userId'))));
    }
    configReady$() {
        return this.configLoaded$;
    }
    clearCache() {
        const cacheManager = DIContainer.getInstance('CacheManager');
        cacheManager.deleteCache('request').subscribe();
        cacheManager.deleteCache('application', this.getAppConfigCacheKey()).subscribe({
            next: () => {
                // Re-fetch and update in-memory state, but don't re-emit configLoaded$ (would re-trigger app.start())
                this.fetchApplicationConfigFromServer({ updateMemory: true, emitConfigLoaded: false });
            }
        });
    }
    getAppConfigCacheKey() {
        return `appConfig_${ENV('appName')}`;
    }
    /**
     * Replaces date-dependent tokens with client-computed values so cached responses
     * stay accurate without a server round-trip. Fiscal tokens are left unchanged
     * since they depend on the FisCalendar table.
     *
     * @see TimePeriods_List.sql for the server-side definitions.
     */
    computeDateTokens(tokens) {
        const now = DateTimeManager.now();
        const fmt = (dt) => dt.toISODate();
        // Week boundaries (Sunday as first day, matching SQL SET DATEFIRST 7 default)
        const weekStartOffset = now.weekday % 7; // Luxon: Mon=1..Sun=7 → Sun→0, Mon→1, ..., Sat→6
        const weekStart = now.minus({ days: weekStartOffset });
        const weekEnd = weekStart.plus({ days: 6 });
        // Month boundaries
        const monthStart = now.startOf('month');
        const monthEnd = now.endOf('month');
        const lastMonthEnd = monthStart.minus({ days: 1 });
        const lastMonthStart = lastMonthEnd.startOf('month');
        const monthBeforeLastEnd = lastMonthStart.minus({ days: 1 });
        const monthBeforeLastStart = monthBeforeLastEnd.startOf('month');
        const nextMonthStart = monthStart.plus({ months: 1 });
        const nextMonthEnd = nextMonthStart.endOf('month');
        // Year boundaries
        const yearStart = now.startOf('year');
        const yearEnd = now.endOf('year');
        const lastYearStart = yearStart.minus({ years: 1 });
        const lastYearEnd = lastYearStart.endOf('year');
        const nextYearStart = yearStart.plus({ years: 1 });
        const nextYearEnd = nextYearStart.endOf('year');
        const computed = new Map([
            // Today
            ['{{TODAY}}', fmt(now)],
            ['{{TODAY_END}}', fmt(now)],
            ['{{YEAR}}', now.toFormat('yyyy')],
            // Week
            ['{{CURRENT_WEEK}}', fmt(weekStart)],
            ['{{CURRENT_WEEK_END}}', fmt(weekEnd)],
            ['{{LAST_WEEK}}', fmt(weekStart.minus({ days: 7 }))],
            ['{{LAST_WEEK_END}}', fmt(weekEnd.minus({ days: 7 }))],
            ['{{NEXT_WEEK}}', fmt(weekStart.plus({ days: 7 }))],
            ['{{NEXT_WEEK_END}}', fmt(weekEnd.plus({ days: 7 }))],
            // Prior/Next N days
            ['{{PRIOR_7_DAYS}}', fmt(now.minus({ days: 7 }))],
            ['{{PRIOR_7_DAYS_END}}', fmt(now.minus({ days: 1 }))],
            ['{{PRIOR_30_DAYS}}', fmt(now.minus({ days: 30 }))],
            ['{{PRIOR_30_DAYS_END}}', fmt(now.minus({ days: 1 }))],
            ['{{PRIOR_60_DAYS}}', fmt(now.minus({ days: 60 }))],
            ['{{PRIOR_60_DAYS_END}}', fmt(now.minus({ days: 1 }))],
            ['{{PRIOR_90_DAYS}}', fmt(now.minus({ days: 90 }))],
            ['{{PRIOR_90_DAYS_END}}', fmt(now.minus({ days: 1 }))],
            ['{{NEXT_7_DAYS}}', fmt(now.plus({ days: 1 }))],
            ['{{NEXT_7_DAYS_END}}', fmt(now.plus({ days: 7 }))],
            // Month
            ['{{MONTH_TO_DATE}}', fmt(monthStart)],
            ['{{MONTH_TO_DATE_END}}', fmt(now)],
            ['{{CURRENT_MONTH}}', fmt(monthStart)],
            ['{{CURRENT_MONTH_END}}', fmt(monthEnd)],
            ['{{CURRENT_12_MONTHS}}', fmt(monthStart)],
            ['{{CURRENT_12_MONTHS_END}}', fmt(monthStart.plus({ months: 12 }).minus({ days: 1 }))],
            ['{{LAST_MONTH}}', fmt(lastMonthStart)],
            ['{{LAST_MONTH_END}}', fmt(lastMonthEnd)],
            ['{{LAST_MONTH_ON_THIS_DATE}}', fmt(now.minus({ months: 1 }))],
            ['{{LAST_MONTH_ON_THIS_DATE_END}}', fmt(now.minus({ months: 1 }))],
            ['{{MONTH_BEFORE_LAST}}', fmt(monthBeforeLastStart)],
            ['{{MONTH_BEFORE_LAST_END}}', fmt(monthBeforeLastEnd)],
            ['{{LAST_12_MONTHS}}', fmt(monthStart.minus({ months: 12 }))],
            ['{{LAST_12_MONTHS_END}}', fmt(lastMonthEnd)],
            ['{{NEXT_MONTH}}', fmt(nextMonthStart)],
            ['{{NEXT_MONTH_END}}', fmt(nextMonthEnd)],
            // Last year month to date
            ['{{LAST_YEAR_MONTH_TO_DATE}}', fmt(monthStart.minus({ years: 1 }))],
            ['{{LAST_YEAR_MONTH_TO_DATE_END}}', fmt(now.minus({ years: 1 }))],
            // Calendar year
            ['{{CALENDAR_YTD}}', fmt(yearStart)],
            ['{{CALENDAR_YTD_END}}', fmt(now)],
            ['{{CALENDAR_YEAR}}', fmt(yearStart)],
            ['{{CALENDAR_YEAR_END}}', fmt(yearEnd)],
            ['{{LAST_CALENDAR_YEAR}}', fmt(lastYearStart)],
            ['{{LAST_CALENDAR_YEAR_END}}', fmt(lastYearEnd)],
            ['{{LAST_CALENDAR_YEAR_ON_THIS_DATE}}', fmt(lastYearStart)],
            ['{{LAST_CALENDAR_YEAR_ON_THIS_DATE_END}}', fmt(now.minus({ years: 1 }))],
            ['{{NEXT_CALENDAR_YEAR}}', fmt(nextYearStart)],
            ['{{NEXT_CALENDAR_YEAR_END}}', fmt(nextYearEnd)],
        ]);
        return tokens.map(token => {
            const value = computed.get(token.tokenFormat);
            return value !== undefined ? { ...token, tokenValue: value } : token;
        });
    }
    /**
     * Cache-first application config loading:
     * Phase 1: Load from 'application' IndexedDB cache. If found, parse immediately and emit configLoaded$.
     * Phase 2: Fetch from server in background to update the cache for next load.
     */
    loadApplicationConfig() {
        // Cancel any previous in-flight load (prevents duplicate fetches across login cycles)
        this.appConfigLoadSub?.unsubscribe();
        const cacheManager = DIContainer.getInstance('CacheManager');
        const cacheKey = this.getAppConfigCacheKey();
        this.appConfigLoadSub = cacheManager.getCache('application', cacheKey, { defaultReturnValue: null })
            .subscribe({
            next: (cachedPayload) => {
                if (cachedPayload) {
                    // Shallow clone with targeted token replacement so we don't mutate the cached object
                    const payload = {
                        ...cachedPayload,
                        tokens: this.computeDateTokens(cachedPayload.tokens || [])
                    };
                    this.parseApplicationConfigResponse(payload);
                }
                // Fetch from server in background (or foreground if no cache)
                this.fetchApplicationConfigFromServer(cachedPayload
                    ? { updateMemory: false, emitConfigLoaded: false }
                    : { updateMemory: true, emitConfigLoaded: true });
            },
            error: () => {
                // Cache read failed, fall back to network-only
                this.fetchApplicationConfigFromServer({ updateMemory: true, emitConfigLoaded: true });
            }
        });
    }
    fetchApplicationConfigFromServer(options) {
        const cacheManager = DIContainer.getInstance('CacheManager');
        const cacheKey = this.getAppConfigCacheKey();
        this.appConfigLoadSub = this.Request.GET({
            endpoint: `application/${ENV('appName')}`,
            requestData: {
                browserlanguage: navigator.language
            },
            showErrors: false,
            cache: false
        }).subscribe({
            next: (response) => {
                // Save only the payload to the application cache (strip HTTP transport wrapper)
                cacheManager.setCache('application', cacheKey, response.payload).subscribe();
                if (options.updateMemory) {
                    this.parseApplicationConfigResponse(response.payload, { emitConfigLoaded: options.emitConfigLoaded });
                }
            },
            error: (err) => {
                if (options.emitConfigLoaded) {
                    // Initial load failed with no cache -- show error screen
                    console.error('loadApplicationConfig failed', err);
                    createComponentsFromDOM(document);
                    DIContainer.getInstance('ScreenManager').open('error', {
                        title: 'Error',
                        requestParams: err
                    });
                }
                else {
                    console.warn('Background application config refresh failed', err);
                }
            }
        });
    }
    parseApplicationConfigResponse(payload, options) {
        const tokens = payload.tokens;
        tokens?.forEach((token) => {
            TOKEN(token.tokenFormat, token.tokenValue);
        });
        const configdata = payload.configdata || [];
        configdata?.forEach((configitem) => {
            CONFIG(configitem.configItem, configitem.configValue1, configitem.configValue2);
        });
        const perms = payload.perms || [];
        perms?.forEach((perms) => {
            PERM(perms.permName, perms.value);
        });
        const captions = payload.captions;
        const captionManager = DIContainer.getInstance('CaptionManager');
        captionManager.addCaptions(captions);
        const currencies = payload.currency;
        if (currencies && DIContainer.isSingletonRegistered('CurrencyManager')) {
            const currencyManager = DIContainer.getInstance('CurrencyManager');
            currencyManager?.addCurrencies(currencies);
        }
        // unsetCookie('XDEBUG_SESSION'); this makes debugging basically impossible when you need to reload the browser to hit a certain network request, dont know why we have this here.
        const userpreferences = payload.userpreferences;
        userpreferences?.forEach((pref) => {
            let prefVal = pref.value;
            if (prefVal === 'true') {
                prefVal = true;
            }
            if (prefVal === 'false') {
                prefVal = false;
            }
            ENV(pref.preference, prefVal);
            if (pref.preference === 'userDatetimeFormat' && pref.value) {
                this.updateDatetimeFormat(pref.value);
            }
            if (pref.preference === 'xdebug' && pref.value) {
                setCookie('XDEBUG_SESSION', 'PHPSTORM');
            }
        });
        this.updateCssVars();
        const environment = payload.environment;
        environment?.forEach((pref) => {
            let prefVal = pref.value;
            if (prefVal === 'true') {
                prefVal = true;
            }
            if (prefVal === 'false') {
                prefVal = false;
            }
            if (pref.variable === 'oAuthConfig') {
                ENV(pref.variable, JSON.parse(pref.value));
            }
            else {
                ENV(pref.variable, prefVal);
            }
        });
        if (ENV('theme')) {
            document.body.setAttribute('data-theme', ENV('theme'));
        }
        if (ENV('sizing')) {
            document.body.setAttribute('data-sizing', ENV('sizing'));
        }
        if (ENV('gridSizing')) {
            document.body.setAttribute('data-grid-sizing', ENV('gridSizing'));
        }
        if (ENV('contrast')) {
            document.body.setAttribute('data-contrast', ENV('contrast'));
        }
        const timezones = payload.timezones;
        if (timezones) {
            const timezoneMap = ENV('timezoneMapping');
            timezones.forEach((tz) => {
                timezoneMap[tz.TZName] = tz.SqlTimezone;
            });
        }
        if (this.authRequired) {
            const timeoutWarningMinutes = !parseInt(CONFIG('SESSIONTIMEOUT', 2)) ? 5 : parseInt(CONFIG('SESSIONTIMEOUT', 2));
            this.SessionTimeoutManager.setWarningThreshold(timeoutWarningMinutes);
        }
        if (options?.emitConfigLoaded !== false) {
            this.configLoaded$.next(true);
        }
    }
    draw() {
        this.componentGet('navbar').captionKey = `${ENV('appName')}_navbar`;
        super.draw();
        this.rootElement.classList.add('s360Application');
        const tooltipManager = DIContainer.getInstance('TooltipManager');
        tooltipManager.draw();
        document.body.appendChild(tooltipManager.rootElement);
        const toastManager = DIContainer.getInstance('ToastManager');
        toastManager.draw();
        document.body.appendChild(toastManager.rootElement);
        // TODO: not sure if we are going to use this methodology at all or if we will just use a mobile css file. 
        if (isMobile()) {
            this.rootElement.classList.add('s360MobileView');
        }
        this.registerEvent('fullscreenchange', document, () => {
            if (this.fullscreenElement) {
                this.fullscreenElement.classList.remove('fullscreen');
            }
            this.fullscreenElement = document.fullscreenElement;
            DIContainer.getInstance('ComponentRegistry').forEach((component) => {
                component.handleFullscreenSwitch(document.fullscreenElement);
            });
        });
        this.registerEvent('beforeunload', window, (e) => {
            let block = false;
            DIContainer.getInstance('ScreenManager').forEachScreen((screen) => {
                if (!block && screen.currentForm) {
                    block = screen.currentForm.hasUnSavedChanges || !!screen.currentForm.closePromptCaptionKey; // if it has a closePromptCaptionKey then its intended to block close with a prompt
                }
            });
            if (block) {
                e.preventDefault();
                e.returnValue = '';
            }
        });
        this.registerEvent('unload', window, () => {
            // send beacon should initiate a request that won't be canceled when the browser tab (or whole browser) closes.
            const apiRoot = ENV('apiRoot');
            DIContainer.getInstance('ScreenManager').forEachScreen((screen) => {
                if (screen.currentForm && screen.currentForm.record?.locked) {
                    const record = screen.currentForm.record;
                    const formData = new FormData();
                    formData.append('tablename', record.tableName);
                    formData.append('primarykey', record.pkValue);
                    navigator.sendBeacon(`${apiRoot}record?_a=unlock`, formData);
                }
            });
        });
        this.initFocusListener();
    }
    updateCssVars() {
        document.body.style.setProperty('--grid-row-bg-ev', ENV('gridAlternateColors') ? '' : 'var(--grid-row-bg)');
        document.body.style.setProperty('--grid-vertical-lines', ENV('gridVerticalLines') ? '1px solid var(--grid-border)' : 'none');
    }
    /**
     * Sets up a global event to delegate focus events for all components in the application.
     * This is a performance/memory optimization to reduce the number of event listeners required by our forms.
     * @protected
     */
    initFocusListener() {
        // Handle focusin event
        this.registerEvent('focusin', document, (event) => {
            this.handleComponentEvent(event, 'focus');
        });
        this.registerEvent('paste', document, (event) => {
            this.handleComponentEvent(event, 'paste');
        });
        // Handle focusout event
        this.registerEvent('focusout', document, (event) => {
            this.handleComponentEvent(event, 'blur');
        });
        this.registerEvent('keydown', document, (event) => {
            if (this.authRequired) {
                this.SessionTimeoutManager.pulse();
            }
            this.handleComponentEvent(event, 'keydown');
        });
        this.registerEvent('click', document, (event) => {
            if (this.authRequired) {
                this.SessionTimeoutManager.pulse();
            }
            let el = event.target;
            if (!el) {
                return;
            }
            el = el.closest('a');
            if (el && !el.dataset.noLinkIntercept) {
                const initiator = DIContainer.getInstance('ComponentRegistry').getComponentByElement(el);
                const anchor = el;
                const href = anchor.getAttribute('href');
                if (initiator && initiator.rootElement.dataset.ctrlClickAnchorInEditmode && !initiator.shouldDisable() && !event.ctrlKey) {
                    return; // if its in edit mode, they must hold the ctrl key to click a hyperlink, so that it's not tedious to edit the link
                }
                if (href) {
                    if (initiator && initiator.componentName === 'Tooltip') {
                        DIContainer.getInstance('TooltipManager').handleClickAway(event);
                    }
                    const lowerHref = href.toLowerCase();
                    if (lowerHref.startsWith('#') ||
                        lowerHref.startsWith('mailto:') ||
                        lowerHref.startsWith('tel:') ||
                        lowerHref.startsWith('sms:')
                    // lowerHref.startsWith('javascript:')
                    ) {
                        return;
                    }
                    event.preventDefault();
                    zoomHandler(href, {
                        target: '_top',
                        initiator,
                        event
                    });
                }
                return;
            }
        });
    }
    /**
     * Delegates an event to the applicable component.
     * @param event
     * @param type
     */
    handleComponentEvent(event, type /*| 'click'*/) {
        const targetElement = event.target;
        const component = DIContainer.getInstance('ComponentRegistry').getComponentByElement(targetElement, true);
        if (type === 'focus' && component && component.handleFocus) {
            this.lastFocusedComponent = component;
            component.handleFocus(event);
        }
        else if (type === 'blur' && component && component.handleBlur) {
            this.lastFocusedComponent = undefined;
            component.handleBlur(event);
        }
        else if (type === 'keydown') {
            if (this.fullscreenElement && event.key === 'escape') {
                this.closeFullscreen();
            }
            else if (this.lastFocusedComponent && this.lastFocusedComponent.handleKeyPress) {
                this.lastFocusedComponent.handleKeyPress(event);
            }
            else {
                this.handleKeyPress(event);
            }
        }
        else if (type === 'paste' && component && typeof component.handlePaste === 'function') {
            component.handlePaste(event);
        }
    }
    blurFocusedComponent() {
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }
    }
    /**
     * Called once when the browser loads initially, after drawing the Application component.
     * requestParams contains the query string parameters from the browser URL.
     * If requestParams (URL) contains a form action it will be opened in the viewport.
     * @param requestParams
     */
    start(requestParams) {
        const hash = window.location.hash;
        const formName = requestParams?._form || '';
        let homepage = isMobile() ? ENV('userMobileHomepage') : ENV('userHomepage');
        if (formName) {
            delete requestParams._form;
            DIContainer.getInstance('ScreenManager').open(formName, { ...{ requestParams }, ...{ hash }, ...{ collapseToolbar: true } });
        }
        else if (homepage) {
            homepage = convertLegacyQ360Url(homepage);
            DIContainer.getInstance('ScreenManager').open(homepage, { ...{ hash } });
        }
        else {
            const screenRouter = DIContainer.getInstance('ScreenRouter');
            if (screenRouter.hasRoute('')) {
                DIContainer.getInstance('ScreenManager').open('', { ...{ requestParams }, ...{ hash } });
            }
        }
    }
    /**
     * This is for application level user preferences, not for filters on a random form or for grids etc.
     * ex: screenMode and theme.
     * @param preference
     * @param value
     * @param appName
     * @example
     * let app = DIContainer.getInstance<Application>('Application');
     * app.saveUserPreference('theme', 'dark');
     */
    saveUserPreference(preference, value, appName) {
        // we might choose to pass appName = '%' for some preferences that we would want to share accross multiple apps, probably just theme.
        appName = appName || ENV('appName');
        Request.PUT({
            endpoint: 'application?_a=preferenceSave',
            loadingMessage: 'Saving preference',
            requestData: {
                'appname': appName ?? 'q360',
                'preference': preference,
                'value': value
            }
        }).subscribe(() => {
            console.log('saved preference', appName ?? 'q360', preference, value);
        });
        this.updateApplicationCachePreference(preference, value);
    }
    updateApplicationCachePreference(preference, value) {
        if (preference === 'datetimeFormat') {
            if (!value) {
                value = ENV('datetimeFormat', DateTimeManager.getLocaleBasedDatetimeFormat());
            }
            ENV(preference, value);
            ENV('dateFormat', DateTimeManager.getPartFromDateTimeFormat(ENV('datetimeFormat'), 'date'));
            ENV('timeFormat', DateTimeManager.getPartFromDateTimeFormat(ENV('datetimeFormat'), 'time'));
        }
        else {
            if (value === 'true') {
                ENV(preference, true);
            }
            else if (value === 'false') {
                ENV(preference, false);
            }
            else {
                ENV(preference, value);
            }
        }
        const cacheManager = DIContainer.getInstance('CacheManager');
        const cacheKey = this.getAppConfigCacheKey();
        cacheManager.getCache('application', cacheKey, { defaultReturnValue: null })
            .subscribe((cachedPayload) => {
            if (!cachedPayload)
                return;
            const prefs = cachedPayload.userpreferences || [];
            const existingIndex = prefs.findIndex((p) => p.preference === preference);
            if (existingIndex >= 0) {
                prefs[existingIndex].value = value;
            }
            else {
                prefs.push({ preference, value });
            }
            cacheManager.setCache('application', cacheKey, cachedPayload).subscribe();
        });
    }
    updateDatetimeFormat(format) {
        if (!format) {
            format = DateTimeManager.getLocaleBasedDatetimeFormat();
        }
        ENV('datetimeFormat', format);
        ENV('dateFormat', DateTimeManager.getPartFromDateTimeFormat(ENV('datetimeFormat'), 'date'));
        ENV('timeFormat', DateTimeManager.getPartFromDateTimeFormat(ENV('datetimeFormat'), 'time'));
        // TODO: trigger components to re-render possibly.
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleDatetimeFormatSwitch(format);
        });
    }
    handleGridSizingSwitch(size) {
        document.body.setAttribute('data-grid-sizing', size);
    }
    handleContrastSwitch(contrast) {
        document.body.setAttribute('data-contrast', contrast);
    }
    updateSizing(size) {
        this.saveUserPreference('sizing', size, '%');
        document.body.setAttribute('data-sizing', size);
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleSizingSwitch(size);
        });
    }
    updateTimezone(zone) {
        this.saveUserPreference('userTimezone', zone);
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleTimezoneSwitch(zone);
        });
    }
    updateTheme(theme) {
        this.saveUserPreference('theme', theme, '%');
        document.body.setAttribute('data-theme', theme);
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleThemeSwitch(theme);
        });
    }
    updateScreenMode(mode) {
        const variable = isMobile() ? 'screenModeMobile' : 'screenMode';
        if (mode && mode !== ENV(variable)) {
            this.saveUserPreference(variable, mode);
        }
        this.components.viewport.updateScreenMode(mode);
    }
    updateMobileMode(enabled) {
        if (enabled === isMobile()) {
            return;
        }
        ENV('mobile', enabled);
        if (enabled && ENV('screenMode') === 'window') {
            ENV('screenMode', 'tab');
            this.components.viewport.updateScreenMode('tab'); // dont want them to lose windows but cant let them have window mode on mobile, so going to tab mode instead of single mode.
        }
        const cacheBuster = Math.floor(Math.random() * 999999);
        const cssPath = `/dist/s360.m.css?${cacheBuster}`; // this is not correct for other apps besides Q360.
        if (!enabled) {
            const links = document.querySelectorAll('link[href^="/dist/s360.m.css"]');
            links.forEach(link => link.parentNode?.removeChild(link));
        }
        else {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = cssPath;
            document.head.appendChild(link);
        }
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleMobileSwitch(enabled);
        });
    }
    /*updateViewMode(mode: 'tech'|'full') {
        if (mode !== ENV('viewMode')) {
            this.saveUserPreference('viewMode', mode);
            
            DIContainer.getInstance<ComponentRegistry>('ComponentRegistry').forEach((component) => {
                component.handleViewSwitch(mode);
            })
        }
    }*/
    /**
     * Logs out the current user.
     *
     * This method sends a POST request to the 'authenticate' endpoint with the 'logout' action.
     * After receiving a response from the server, it reloads the current window.
     *
     * @example
     * s360.Application.getInstance().logout();
     *
     * @remarks
     * The method uses the `Request.POST` method to initiate the logout process.
     * Once the logout is successful, the browser window is reloaded to ensure the user is sent to loginform.
     */
    logout() {
        const request = this.Request.POST({
            endpoint: 'authenticate?_a=logout',
            loadingMessage: 'Logging out...'
        });
        this.validateLogout().pipe(switchMap((valid) => valid ? request : of(false))).subscribe((loggedOut) => {
            if (loggedOut) {
                const redirectURL = '/' + ENV('appName');
                window.location.assign(redirectURL);
            }
        });
    }
    reset() {
    }
    validateLogout() {
        return of(true);
    }
    openFullscreen(element) {
        if (element.requestFullscreen) {
            element.requestFullscreen();
            element.classList.add('fullscreen');
        }
    }
    closeFullscreen() {
        if (document.fullscreenElement && document.exitFullscreen) {
            document.exitFullscreen();
        }
    }
    template() {
        if (isMobile()) {
            this.rootElement.classList.add('s360MobileView');
        }
        const template = `
<span data-componentid="navbar" ></span>
<div class="main-container">
	<span data-componentid="viewport" ></span>
</div>
`;
        return template;
    }
    /**
     * Static method to retrieve the singleton instance of `Application`.
     * Shorthand for DIContainer syntax.
     * @returns The singleton instance of `Application`.
     *
     * @example
     * // in devtools console
     * const app = s360.Application.getInstance();
     * app.open(...);
     */
    static getInstance() {
        // keeping getInstance on this class primarily just for convenience so it can be accessed in the console more easily.
        return DIContainer.getInstance('Application');
    }
    /**
     * Creates a Panel, adds a toggle button to the navbar's right-menu,
     * and registers the panel in the panelset.
     */
    addNavbarPanel(panelId, buttonIcon, panelComponent) {
        const panelComponentId = `${panelId}_panel`;
        const panel = DIContainer.new('Panel', panelComponentId);
        const panelset = this.componentGet('panelset');
        const navbar = this.componentGet('navbar');
        panel.captionKey = `q360_${panelComponentId}`;
        navbar.buttonAdd({
            id: `${panelComponentId}_button`,
            icon: buttonIcon,
            containerId: 'right-menu',
            index: -999,
            captionKey: `q360_navbar_${panelId}`,
            onClick: () => {
                panelset.togglePanel(panelComponentId);
                if (isMobile()) {
                    this.componentGet('toolbar')?.propertySet('collapsed', true);
                }
            }
        });
        panel.componentAdd(panelComponent);
        panelset.componentAdd(panel, true);
    }
    /**
     * Subscribes to a notification type and updates a navbar button's
     * badge count and `new-content` class accordingly.
     */
    bindNavbarBadge(notificationType, buttonId) {
        const navbar = this.componentGet('navbar');
        DIContainer.getInstance('NotificationManager').listen(notificationType).subscribe((notifications) => {
            const count = notifications.length;
            navbar.buttonClassListUpdate(buttonId, 'new-content', !!count);
            navbar.buttonAttributesSet(buttonId, {
                'num-notifications': count > 99 ? '!' : count.toString()
            });
        });
    }
}
Application.VERSION = '$Revision: 130860 $';
